import { useEffect, useRef, useState } from "react";

import { listUsers } from "@/lib/admin-api";

import styles from "./principal-search.module.css";

/**
 * PrincipalSearch resolves a person to a principal id at runtime.
 *
 * It exists because of the single hardest rule on this feature: NO PRINCIPAL
 * UUID MAY BE HARDCODED ANYWHERE. Principals are seeded per environment, so an
 * id baked into code, config or a test fixture is correct in at most one place
 * and silently wrong everywhere else — and "wrong" here means granting the
 * right to publish sponsored content to whoever happens to hold that id in
 * staging. Every id this panel sends comes out of this component, from a live
 * user-service search.
 *
 * The interaction mirrors the combobox on the Authorized Publishers page
 * (moderation/authorized-publishers.tsx) so the two searches behave the same:
 * debounce, arrow keys, Enter to select, Escape to close. It searches USERS
 * only — an advertiser account is a user, and offering squads here would let an
 * operator grant to a squad principal that auth-service stores but never
 * resolves (its capability lookup is by user id alone).
 */
export interface PrincipalOption {
  id: string;
  handle?: string;
  displayName?: string;
}

export function principalLabel(option: PrincipalOption): string {
  return option.displayName || (option.handle ? `@${option.handle}` : option.id);
}

const MIN_SEARCH_LENGTH = 2;

function useDebouncedValue<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export interface PrincipalSearchProps {
  id: string;
  label: string;
  hint?: string;
  selected: PrincipalOption | null;
  onSelect: (option: PrincipalOption | null) => void;
  placeholder?: string;
}

export function PrincipalSearch({
  id,
  label,
  hint,
  selected,
  onSelect,
  placeholder = "Search by handle or name…",
}: PrincipalSearchProps) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<PrincipalOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  // Set just before a selection rewrites `query` to the chosen handle, so the
  // search effect does not treat that write as a new query and reopen the
  // dropdown the selection just closed.
  const justSelected = useRef(false);
  const debounced = useDebouncedValue(query.trim());
  const listboxId = `${id}-listbox`;

  useEffect(() => {
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }

    const normalised = debounced.replace(/^@/, "");
    if (normalised.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    listUsers({ query: normalised, pageSize: 8, page: 0 })
      .then((data) => {
        if (cancelled) return;
        setOptions(
          (data.users ?? [])
            .filter((user) => !!user.id)
            .map((user) => ({
              id: user.id,
              handle: user.username,
              displayName: user.displayName,
            })),
        );
        setActiveIndex(-1);
        setOpen(true);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setOptions([]);
        setError(err.message ?? "Search failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debounced]);

  function select(option: PrincipalOption) {
    justSelected.current = true;
    onSelect(option);
    setQuery(option.handle ? `@${option.handle}` : principalLabel(option));
    setOpen(false);
    setOptions([]);
    setActiveIndex(-1);
  }

  function clear() {
    onSelect(null);
    setQuery("");
    setOptions([]);
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || options.length === 0) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % options.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => (index <= 0 ? options.length - 1 : index - 1));
        break;
      case "Enter":
        if (activeIndex >= 0 && activeIndex < options.length) {
          event.preventDefault();
          select(options[activeIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  }

  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      {hint ? <p className={styles.fieldHint}>{hint}</p> : null}

      <div className={styles.comboboxWrap}>
        <input
          id={id}
          className={styles.input}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          placeholder={placeholder}
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            if (selected) onSelect(null);
          }}
          onKeyDown={handleKeyDown}
        />
        {selected ? (
          <button type="button" className={styles.clearButton} onClick={clear}>
            Clear
          </button>
        ) : null}

        {open && options.length > 0 ? (
          <ul className={styles.listbox} id={listboxId} role="listbox">
            {options.map((option, index) => (
              <li key={option.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={index === activeIndex ? styles.optionActive : styles.option}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(option)}
                >
                  <span className={styles.optionName}>{principalLabel(option)}</span>
                  {option.handle ? (
                    <span className={styles.optionHandle}>@{option.handle}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {loading ? <span className={styles.fieldHint}>Searching…</span> : null}
      {error ? <span className={styles.errorText}>{error}</span> : null}
      {selected ? (
        // The resolved id is shown, not hidden: it is what actually gets sent,
        // and an operator granting a commercial permission should be able to
        // see exactly which principal they picked.
        <span className={styles.selectedId}>
          Selected: {principalLabel(selected)} · <code>{selected.id}</code>
        </span>
      ) : null}
    </div>
  );
}
